import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";
import { IMAGES } from "@/lib/data";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden pt-28 pb-16">
      {/* Parallax accent image */}
      <motion.div style={{ y, scale }} className="absolute -right-24 top-10 w-[46vw] max-w-[720px] aspect-[3/4] hidden md:block">
        <div className="relative h-full w-full overflow-hidden" style={{ clipPath: "polygon(12% 0, 100% 0, 100% 100%, 0 100%)" }}>
          <img src={IMAGES.consultation} alt="Consultation" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-navy/25" />
        </div>
      </motion.div>

      <div className="relative mx-auto max-w-7xl w-full px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-8">
          Faith &amp; Grace Advisory Services
        </motion.p>

        <h1 className="font-serif font-light tracking-tight leading-[0.95] text-primary text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl">
          <Line delay={0.45}>Turning purpose</Line>
          <Line delay={0.6}>into <span className="italic text-gold">strategy</span>, and strategy</Line>
          <Line delay={0.75}>into lasting impact.</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-12 max-w-3xl">
          <p className="text-base md:text-lg font-light leading-relaxed text-muted-foreground max-w-md">
            We help nonprofits, small businesses, and ministries secure funding, strengthen leadership, and build organizations that endure.
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-consultation-btn" onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm hover:bg-[hsl(var(--gold))] hover:text-primary transition-colors duration-300">
              Schedule Your Free Consultation
              <ArrowDownRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </button>
            <button data-testid="hero-services-btn" onClick={() => scrollTo("#services")}
              className="rounded-full border border-primary/25 text-primary px-7 py-4 text-sm hover:border-[hsl(var(--gold))] transition-colors duration-300">
              Explore Services
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
